export interface Comment {
  t: number; // createdAt (ms)
  text: string;
  author?: string;
  offsetMs?: number; // момент трека, к которому комментарий
}
export interface Reacts {
  fire: number;
  star: number;
}

interface SocialState {
  comments: Comment[];
  counts: { fire: number; star: number; like: number };
  mine: { fire: boolean; star: boolean; like: boolean };
}
const EMPTY: SocialState = {
  comments: [],
  counts: { fire: 0, star: 0, like: 0 },
  mine: { fire: false, star: false, like: false },
};

/**
 * Соц-данные (комменты/реакции/лайки) через бэкенд `/api/social` — анонимно,
 * по сгенерированному `client_id` (localStorage). Кэш на трек + оптимистичные
 * обновления; ответ сервера перезаписывает кэш истиной. Ключ — trackId (не слот):
 * соц-данные общие для всех слушателей трека (это радио, не персональный плейлист).
 */
export class Social {
  private clientId: string;
  private cache = new Map<number, SocialState>();
  private cbs: Array<() => void> = [];
  private readonly base = '/api/social';

  constructor() {
    let cid = '';
    try {
      cid = localStorage.getItem('grv-pfm-cid') || '';
    } catch {
      /* noop */
    }
    if (!cid) {
      cid = 'c-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      try {
        localStorage.setItem('grv-pfm-cid', cid);
      } catch {
        /* noop */
      }
    }
    this.clientId = cid;
  }

  onChange(cb: () => void): void {
    this.cbs.push(cb);
  }
  private notify(): void {
    for (const cb of this.cbs) cb();
  }
  private set(trackId: number, s: SocialState): void {
    this.cache.set(trackId, s);
    this.notify();
  }
  private stateOf(trackId: number): SocialState {
    return this.cache.get(trackId) || EMPTY;
  }

  /** Подтянуть соц-данные трека с бэкенда (вызывать при смене трека). */
  async load(trackId: number): Promise<void> {
    try {
      const r = await fetch(`${this.base}/${trackId}?clientId=${encodeURIComponent(this.clientId)}`, {
        cache: 'no-store',
      });
      if (r.ok) this.set(trackId, this.norm(await r.json()));
    } catch {
      /* офлайн — оставляем кэш */
    }
  }

  private norm(j: {
    comments?: Array<{ createdAt: number; text: string; author?: string; offsetMs?: number }>;
    counts?: { fire?: number; star?: number; like?: number };
    mine?: { fire?: boolean; star?: boolean; like?: boolean };
  }): SocialState {
    return {
      comments: (j.comments || []).map((c) => ({
        t: c.createdAt,
        text: c.text,
        author: c.author,
        offsetMs: c.offsetMs,
      })),
      counts: { fire: j.counts?.fire || 0, star: j.counts?.star || 0, like: j.counts?.like || 0 },
      mine: { fire: !!j.mine?.fire, star: !!j.mine?.star, like: !!j.mine?.like },
    };
  }

  // ---- sync-геттеры для render ----
  reactOf(trackId: number): Reacts {
    const s = this.stateOf(trackId);
    return { fire: s.counts.fire, star: s.counts.star };
  }
  cmtsOf(trackId: number): Comment[] {
    return this.stateOf(trackId).comments;
  }
  isLiked(trackId: number): boolean {
    return this.stateOf(trackId).mine.like;
  }

  // ---- действия (оптимистично + POST) ----
  toggleLike(trackId: number): boolean {
    const willLike = !this.isLiked(trackId);
    void this.react(trackId, 'like');
    return willLike; // для анимации сердечек сразу
  }
  addReact(trackId: number, type: 'fire' | 'star'): void {
    void this.react(trackId, type);
  }

  private async react(trackId: number, kind: 'fire' | 'star' | 'like'): Promise<void> {
    const cur = this.stateOf(trackId);
    const had = cur.mine[kind];
    this.set(trackId, {
      comments: cur.comments,
      counts: { ...cur.counts, [kind]: Math.max(0, cur.counts[kind] + (had ? -1 : 1)) },
      mine: { ...cur.mine, [kind]: !had },
    });
    try {
      const r = await fetch(`${this.base}/${trackId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, client_id: this.clientId }),
      });
      if (r.ok) this.set(trackId, this.norm(await r.json()));
    } catch {
      /* офлайн — оставляем оптимистичное */
    }
  }

  async addComment(trackId: number, text: string, offsetMs = 0): Promise<void> {
    const clean = text.trim().slice(0, 500);
    if (!clean) return;
    const cur = this.stateOf(trackId);
    this.set(trackId, {
      ...cur,
      comments: [...cur.comments, { t: Date.now(), text: clean, offsetMs }],
    });
    try {
      const r = await fetch(`${this.base}/${trackId}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: clean, offset_ms: offsetMs, client_id: this.clientId }),
      });
      if (r.ok) this.set(trackId, this.norm(await r.json()));
    } catch {
      /* офлайн */
    }
  }
}

export function fmtClock(t: number): string {
  const d = new Date(t);
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

export function fmtTime(s: number): string {
  s = Math.max(0, Math.floor(s));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
